"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { SettingsButton } from "@/components/settings/SettingsProvider";
import {
  Crown,
  Menu as MenuIcon,
  User,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { SignInButton, SignedIn, SignedOut } from "@clerk/nextjs";
import CustomUserButton from "./CustomUserButton";
import { InAppSignInButton } from "./InAppBrowserGuide";

import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/notifications/NotificationBell";
import {
  NavBadgePill,
  NavPingDot,
  NavStaticDot,
} from "@/components/nav-badges";
import {
  CODING_EXPECTED_QUESTION_LINKS,
  NAV_UPDATE_SIGNAL_VISIBILITY,
  NAV_SECTIONS,
  STUDY_PLAN_LINKS,
  UPDATED_PAST_EXAM_REVIEW_LINK,
  type NavSubLink,
} from "@/lib/nav-sections";
import {
  usePassSalesClosingSoon,
  withTimedNavBadge,
} from "@/lib/nav-timed-badges";
import { cn } from "@/lib/utils";

/**
 * AuthSection — Clerk auth(SignedOut/SignedIn) 영역 전체를 클라이언트 마운트 후에만 렌더링하는 가드 컴포넌트.
 *
 * 배경: `<SignedOut>` / `<SignedIn>`(Clerk)은 user state가 결정되기 전 hydration 시점에
 * children 가시성을 토글하면서 SSR/CSR 트리 mismatch를 일으킬 수 있다.
 * 특히 `useUser()`를 호출하는 페이지(`/membership` 등)에서 Clerk auth가 일찍 fetch되어
 * `<SignedOut>` 안의 `<Button>` native 요소가 CSR에만 등장하는 mismatch가 재현됨.
 *
 * 해결: 마운트 전에는 SSR/CSR 모두 동일한 정적 `<Button>` placeholder만 렌더링하고,
 * 마운트 후에만 Clerk 트리(`<SignedOut>` + `<SignedIn>`)를 그린다.
 *
 * 디자인은 기존 `<Button variant="ghost" size="icon">` + `<User />` 아이콘 그대로 유지.
 */
export function AuthSection({
  iconClassName,
  showCustomMenuItems = true,
}: {
  iconClassName: string;
  /** CustomUserButton에 전달. 데스크탑은 false로 Clerk 기본 항목만 노출 (JCG-2160) */
  showCustomMenuItems?: boolean;
}) {
  // useSyncExternalStore로 SSR/CSR mismatch 없이 mount 여부 판단 (JCG-2035)
  const mounted = React.useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );

  // 마운트 전: SSR 결과와 1:1 동일한 정적 placeholder만 렌더링
  // (Clerk 트리·InAppSignInButton 모두 회피)
  if (!mounted) {
    return (
      <Button variant="ghost" size="icon" aria-hidden="true">
        <User className={iconClassName} />
        <span className="sr-only">로그인</span>
      </Button>
    );
  }

  return (
    <>
      <SignedOut>
        <InAppSignInButton
          fallback={
            <Button variant="ghost" size="icon">
              <User className={iconClassName} />
              <span className="sr-only">로그인</span>
            </Button>
          }
        >
          <SignInButton mode="modal">
            <Button variant="ghost" size="icon">
              <User className={iconClassName} />
              <span className="sr-only">로그인</span>
            </Button>
          </SignInButton>
        </InAppSignInButton>
      </SignedOut>
      <SignedIn>
        <CustomUserButton showCustomMenuItems={showCustomMenuItems} />
      </SignedIn>
    </>
  );
}
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

function ListItem({
  title,
  children,
  href,
  badge,
  iconSrc,
  ...props
}: React.ComponentPropsWithoutRef<"li"> & {
  href: string;
  badge?: NavSubLink["badge"];
  iconSrc?: string;
}) {
  const isExternal = href.startsWith('http');
  const content = (
    <>
      {iconSrc || badge ? (
        <div
          className={cn(
            "flex items-center gap-2 text-sm font-medium",
            iconSrc ? "leading-tight" : "leading-none",
            badge && "justify-between",
          )}
        >
          {iconSrc && (
            <Image
              src={iconSrc}
              alt=""
              width={24}
              height={30}
              aria-hidden="true"
              className="h-6 w-6 shrink-0 object-contain"
            />
          )}
          <span
            className={cn(
              "min-w-0 flex-1",
              badge ? "truncate" : "break-words",
            )}
          >
            {title}
          </span>
          {badge && <NavBadgePill badge={badge} />}
        </div>
      ) : (
        <div className="text-sm leading-none font-medium">{title}</div>
      )}
      <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
        {children}
      </p>
    </>
  );
  
  return (
    <li {...props}>
      <NavigationMenuLink asChild>
        {isExternal ? (
          <a href={href} target="_blank" rel="noopener noreferrer">
            {content}
          </a>
        ) : (
          <Link href={href}>
            {content}
          </Link>
        )}
      </NavigationMenuLink>
    </li>
  );
}

function LanguageLogo({ src, className }: { src: string; className?: string }) {
  return (
    <Image
      src={src}
      alt=""
      width={24}
      height={24}
      aria-hidden="true"
      className={cn("h-6 w-6 shrink-0 rounded-full object-contain", className)}
    />
  );
}

function getNavSubLinkKey(item: NavSubLink): string {
  return item.href ?? item.label;
}

function CodingExpectedQuestionsItem() {
  const [open, setOpen] = React.useState(false);

  return (
    <li className="rounded-md bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="coding-expected-question-menu"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-start justify-between gap-2 rounded-md px-3 py-2 text-left transition-colors hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none dark:hover:bg-amber-900/60"
      >
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 text-sm leading-tight font-medium">
            <span>코딩 압축 공부법</span>
            {NAV_UPDATE_SIGNAL_VISIBILITY.codingStudy && (
              <NavBadgePill badge="UPDATE" />
            )}
          </span>
          <span className="mt-1 line-clamp-2 block text-sm leading-snug text-amber-700/80 dark:text-amber-200/80">
            26년 3회 언어별 공부 범위
          </span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div
          id="coding-expected-question-menu"
          className="grid grid-cols-2 gap-1 px-2 pb-2"
        >
          {CODING_EXPECTED_QUESTION_LINKS.map((item) => {
            if (!item.href || !item.iconSrc) return null;

            return (
              <NavigationMenuLink key={item.href} asChild>
                <Link
                  href={item.href}
                  className="flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:outline-none dark:text-amber-300 dark:hover:bg-amber-900/60"
                >
                  <LanguageLogo src={item.iconSrc} />
                  <span className="truncate">{item.label}</span>
                </Link>
              </NavigationMenuLink>
            );
          })}
        </div>
      )}
    </li>
  );
}

function MobileNavItem({ item }: { item: NavSubLink }) {
  const itemKey = getNavSubLinkKey(item);
  const highlightClassName =
    "bg-amber-50 font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300";

  if (item.children?.length) {
    return (
      <DropdownMenuSub key={itemKey}>
        <DropdownMenuSubTrigger
          className={cn(item.highlight && highlightClassName)}
        >
          <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
            <span className="truncate">{item.label}</span>
            {item.badge && <NavBadgePill badge={item.badge} tone={item.badgeTone} />}
          </span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {item.children.map((child) => {
            if (!child.href) return null;

            return (
              <DropdownMenuItem key={child.href} asChild>
                <Link
                  href={child.href}
                  className="flex min-w-0 items-center gap-2"
                >
                  {child.iconSrc && <LanguageLogo src={child.iconSrc} />}
                  <span className="truncate">{child.label}</span>
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  if (!item.href) return null;

  // 기존 일반 과목 행까지 이미지 메뉴로 확장하지 않고, 강조 항목에 지정한 아이콘만 노출한다.
  const itemLabel = item.highlight && item.iconSrc ? (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <LanguageLogo src={item.iconSrc} />
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
    </span>
  ) : (
    item.label
  );

  return (
    <DropdownMenuItem
      key={itemKey}
      asChild
      className={cn(item.highlight && highlightClassName)}
    >
      {item.external ? (
        <a href={item.href} target="_blank" rel="noopener noreferrer">
          {itemLabel}
        </a>
      ) : (
        <Link href={item.href}>
          {item.badge ? (
            <span className="flex w-full items-center justify-between gap-2">
              {itemLabel}
              <NavBadgePill badge={item.badge} tone={item.badgeTone} />
            </span>
          ) : (
            itemLabel
          )}
        </Link>
      )}
    </DropdownMenuItem>
  );
}

export default function Menu() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const passSalesClosingSoon = usePassSalesClosingSoon();

  React.useEffect(() => {
    const root = document.documentElement;
    const { body } = document;

    root.toggleAttribute("data-mobile-navbar-overlay", mobileMenuOpen);
    body.toggleAttribute("data-mobile-navbar-overlay", mobileMenuOpen);

    return () => {
      root.removeAttribute("data-mobile-navbar-overlay");
      body.removeAttribute("data-mobile-navbar-overlay");
    };
  }, [mobileMenuOpen]);

  return (
    <>
      {/* Desktop Menu */}
      {/* 데스크탑 NavigationMenu 는 폭 ~825px 로 셸 게이트(1024px) 미만에선 수평 overflow 를 일으키므로 lg 부터만 노출 — 640~1023px 태블릿은 모바일 드롭다운 사용(JCG-2224). */}
      <div className="hidden lg:block">
        <NavigationMenu viewport={false}>
          <NavigationMenuList>
            <NavigationMenuItem>
              {/* 파이널 새 회차 UPDATE 캠페인 토글이 켜지면 은은한 amber 배경 + 도트를 함께 노출한다.
                  amber 링(ring-2)은 menu-grid.md §4-5 L3 카드(커뮤니티 복원) 전용이라 트리거엔 쓰지 않는다.
                  트리거와 목적지 카드에는 새 회차의 붉은 UPDATE 뱃지를 표시한다. */}
              <NavigationMenuTrigger
                className={
                  NAV_UPDATE_SIGNAL_VISIBILITY.mockExam
                    ? "bg-amber-50 hover:bg-amber-100 dark:bg-amber-950 dark:hover:bg-amber-900/60"
                    : undefined
                }
              >
                <span className="inline-flex items-center gap-1">
                  <Crown className="h-4 w-4 shrink-0 text-amber-500" />
                  모의고사
                  {NAV_UPDATE_SIGNAL_VISIBILITY.mockExam && (
                    <NavBadgePill badge="UPDATE" tone="red" />
                  )}
                </span>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid gap-4 w-[420px] grid-cols-2 p-2">
                  <li>
                    <NavigationMenuLink asChild>
                      {/* 배경을 /final-mock-exam 페이지의 회차 토글 컨테이너(amber-200/80 border + 크림 wash)와
                          맞춰 시각적으로 연결. 과하지 않게 연한 amber 프레임만. dark 페어 필수. */}
                      <Link
                        className="flex h-full w-full flex-col justify-end rounded-md border border-amber-200/80 bg-amber-50/70 p-6 no-underline outline-hidden select-none focus:shadow-md dark:border-amber-900/40 dark:bg-amber-950/20"
                        href="/final-mock-exam"
                      >
                        <Image
                          src="/main/final-mock-exam_gamja.webp"
                          alt="파이널 모의고사"
                          width={100}
                          height={100}
                        />
                        <div className="mt-4 mb-2 flex items-center gap-2 text-base font-medium">
                          파이널 모의고사
                          {NAV_UPDATE_SIGNAL_VISIBILITY.mockExam && (
                            <NavBadgePill badge="UPDATE" tone="red" />
                          )}
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          시험 직전 점검용<br />파이널 풀 20문제
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/mock-exam"
                      >
                        <Image
                          src="/main/premium-mock-exam_gamja.webp"
                          alt="프리미엄 모의고사"
                          width={100}
                          height={100}
                        />
                        <div className="mt-4 mb-2 text-base font-medium">
                          프리미엄 모의고사
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          26년 3회 예상 범위<br />랜덤 20문제
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
           <NavigationMenuItem>
           <NavigationMenuTrigger>
             <span className="inline-flex items-center gap-1">
               실기 기출
               {NAV_UPDATE_SIGNAL_VISIBILITY.pastExam && (
                 <NavPingDot className="relative ml-0.5 h-1.5 w-1.5" />
               )}
             </span>
           </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid gap-2 w-[550px] grid-cols-[auto_auto_auto]">
                  <li className="row-span-4">
                    <NavigationMenuLink asChild>
                      <Link
                        className="flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b from-amber-50 to-amber-100 p-4 text-amber-700 no-underline outline-hidden select-none focus:shadow-md dark:from-amber-950 dark:to-amber-900/60 dark:text-amber-300"
                        href="/past-exam"
                      >
                        <Image
                          src="/past-exam/past-exam-clock_gamja.webp"
                          alt="기출 문제 모음"
                          width={90}
                          height={114}
                        />
                        <div className="mt-2 mb-1 text-lg font-medium">
                          기출 문제 + 후기
                        </div>
                        <p className="text-sm leading-tight text-amber-700/80 dark:text-amber-200/80">
                          2020년부터 2026년까지 기출 문제와 후기가 있어요
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <ListItem
                    href={UPDATED_PAST_EXAM_REVIEW_LINK.href}
                    title={UPDATED_PAST_EXAM_REVIEW_LINK.label}
                    badge={UPDATED_PAST_EXAM_REVIEW_LINK.badge}
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    복원 20문제·난이도 분석
                  </ListItem>
                  <ListItem
                    href="/past-exam/2026"
                    title="2026년 기출 문제"
                    badge={
                      NAV_UPDATE_SIGNAL_VISIBILITY.pastExam
                        ? "UPDATE"
                        : undefined
                    }
                  >
                    1·2회 복원 문제 모음
                  </ListItem>
                  <ListItem href="/past-exam/2025" title="2025년 기출 문제">
                    미친 난이도의 1회
                  </ListItem>
                  <ListItem href="/past-exam/2024" title="2024년 기출 문제">
                    꿀같은 1회 하지만..
                  </ListItem>
                  <ListItem href="/past-exam/2023" title="2023년 기출 문제">
                    정처기 문제 기조 정착시기
                  </ListItem>
                  <ListItem href="/past-exam/2022" title="2022년 기출 문제">
                    변화의 끝에 있던 시기
                  </ListItem>
                  <ListItem href="/past-exam/2021" title="2021년 기출 문제">
                    무난했던 한해
                  </ListItem>
                  <ListItem href="/past-exam/2020" title="2020년 기출 문제">
                    출제 기준 변경 첫해!
                  </ListItem>
                </ul>
              </NavigationMenuContent>
           </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuTrigger>
                <span className="inline-flex items-center gap-1">
                  실기 이론
                  {NAV_UPDATE_SIGNAL_VISIBILITY.theoryStudy && (
                    <NavPingDot className="relative ml-0.5 h-1.5 w-1.5" />
                  )}
                </span>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid gap-2 w-[550px] grid-cols-[auto_auto_auto]">
                  <li className="row-span-6">
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/theory"
                      >
                        <Image
                          src="/main/study_gamza.webp"
                          alt="home"
                          width={125}
                          height={125}
                        />
                        <div className="mt-4 mb-2 text-lg font-medium">
                          실기 이론 공부법
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          효율적인 정보처리기사<br />실기 공부법
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <ListItem href="/theory/potato-theory-quiz" title="실기 이론 실전 퀴즈" className="rounded-md bg-amber-50 dark:bg-amber-950">
                    랜덤퀴즈로 실력측정
                  </ListItem>
                  <ListItem
                    href="/theory/potato-theory-hard-quiz"
                    title="실기 이론 매운감자퀴즈"
                    iconSrc="/main/spicy_gamja_v2.webp"
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    Premium 이상 전용 퀴즈
                  </ListItem>
                  <ListItem
                    href="/theory/theory-expected-questions"
                    title="이론 압축 공부법"
                    badge={NAV_UPDATE_SIGNAL_VISIBILITY.theoryStudy ? "UPDATE" : undefined}
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    26년 3회 이론 압축공부법
                  </ListItem>
                  <ListItem href="/theory/jeongcheogi-difficulty-theory" title="이론 난이도 체감" className="rounded-md bg-amber-50 dark:bg-amber-950">
                    상·중·하 난이도 직접 체감
                  </ListItem>
                  <ListItem
                    href={STUDY_PLAN_LINKS.theory.href}
                    title={STUDY_PLAN_LINKS.theory.label}
                    badge={STUDY_PLAN_LINKS.theory.badge}
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    26년 3회 4주 공부 계획
                  </ListItem>
                  <ListItem
                    href={STUDY_PLAN_LINKS.warrior.href}
                    title={STUDY_PLAN_LINKS.warrior.label}
                    badge={STUDY_PLAN_LINKS.warrior.badge}
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    10/12 ~ 24 이론·코딩 집중 계획
                  </ListItem>
                  <ListItem href="/theory/db" title="DB">
                    DB 이론
                  </ListItem>
                  <ListItem href="/theory/network-os" title="네트워크/OS">
                    어려운 문제가 많이 출제되는 부분!
                  </ListItem>
                  <ListItem href="/theory/sw-dev" title="SW 개발">
                    많이 나오는 테스트가 속해있어요
                  </ListItem>
                  <ListItem href="/theory/sw-design" title="SW 설계">
                    무조건 나오는 디자인패턴 포함~
                  </ListItem>
                  <ListItem href="/theory/security-newtech" title="보안/신기술">
                    버리고 가는게 나을 수도 있어요
                  </ListItem>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuTrigger>
                <span className="inline-flex items-center gap-1">
                  실기 코딩
                  {NAV_UPDATE_SIGNAL_VISIBILITY.codingStudy && (
                    <NavPingDot className="relative ml-0.5 h-1.5 w-1.5" />
                  )}
                </span>
              </NavigationMenuTrigger>
              <NavigationMenuContent className="right-0 left-auto">
                <ul className="grid gap-2 w-[670px] grid-cols-[auto_auto_auto]">
                  <li className="row-span-6">
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/coding"
                      >
                        <Image
                          src="/coding/c-language/c-language_gamja.webp"
                          alt="실기 코딩"
                          width={125}
                          height={125}
                          className="self-center"
                        />
                        <div className="mt-1 mb-1 text-lg font-medium">
                          실기 코딩
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          정보처리기사 실기 코딩<br />문제 풀이를 위한 이론
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <ListItem href="/coding/potato-coding-quiz" title="실기 코딩 실전 퀴즈" className="rounded-md bg-amber-50 dark:bg-amber-950">
                    랜덤퀴즈로 실력측정
                  </ListItem>
                  <ListItem
                    href="/coding/potato-coding-hard-quiz"
                    title="실기 코딩 매운감자 퀴즈"
                    iconSrc="/main/spicy_gamja_v2.webp"
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    Premium 이상 전용 퀴즈
                  </ListItem>
                  <ListItem
                    href={STUDY_PLAN_LINKS.coding.href}
                    title={STUDY_PLAN_LINKS.coding.label}
                    badge={STUDY_PLAN_LINKS.coding.badge}
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    26년 3회 6주 공부 계획
                  </ListItem>
                  <ListItem
                    href={STUDY_PLAN_LINKS.warrior.href}
                    title={STUDY_PLAN_LINKS.warrior.label}
                    badge={STUDY_PLAN_LINKS.warrior.badge}
                    className="rounded-md bg-amber-50 dark:bg-amber-950"
                  >
                    10/12 ~ 24 이론·코딩 집중 계획
                  </ListItem>
                  <CodingExpectedQuestionsItem />
                  <ListItem href="/coding/jeongcheogi-difficulty-coding" title="코딩 난이도 체감" className="rounded-md bg-amber-50 dark:bg-amber-950">
                    상·중·하 난이도 직접 체감
                  </ListItem>
                  <ListItem href="/coding/common" title="공통 기초">
                    진법, 아스키코드가 핵심이에요
                  </ListItem>
                  <ListItem href="/coding/c-language" title="C언어">
                    포인터 잡으면 절반은 성공!
                  </ListItem>
                  <ListItem href="/coding/java" title="Java">
                    상속·오버라이딩 꼭 익혀두세요
                  </ListItem>
                  <ListItem href="/coding/python" title="Python">
                    비교적 쉬운 편, 꼭 챙기세요
                  </ListItem>
                  <ListItem href="/coding/sql" title="SQL">
                    SELECT·JOIN·DML이 핵심이에요
                  </ListItem>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuTrigger>시험 정보</NavigationMenuTrigger>
              <NavigationMenuContent className="right-0 left-auto">
                <ul className="grid gap-2 w-[400px] lg:grid-cols-[.75fr_1fr]">
                  <li className="row-span-7">
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/exam-registration/jcg-test-center"
                      >
                        <Image
                          src="/exam-registration/jcg-test-center/test-center_gamza.webp"
                          alt="home"
                          width={125}
                          height={125}
                        />
                        <div className="mt-4 mb-2 text-lg font-medium">
                          시험장 찾기
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          정처기 실기 시험장을 지도로 찾아보세요
                        </p>
                        </Link>
                    </NavigationMenuLink>
                  </li>
                  <ListItem
                    href="/exam-registration/jeongcheogi-pass-rate"
                    title="정처기 합격률 모음"
                  >
                    2020년부터 합격률 추이 볼까요
                  </ListItem>

                  <ListItem
                    href="/exam-registration/jeongcheogi-pre-application"
                    title="정처기 원서 사전 입력"
                  >
                    실기 사전입력·시험장 선점 방법을 확인하세요
                  </ListItem>

                  <ListItem
                    href="/exam-registration/jeongcheogi-written-exam-strategy"
                    title="정처기 필기 시험일 선택 전략"
                  >
                    내 상황에 맞는 필기 시험일 고르는 법
                  </ListItem>

                  <ListItem
                    href="/exam-registration/jeongcheogi-study-period"
                    title="정처기 준비기간"
                  >
                    필기·실기 공부기간과 시작 시점 정하기
                  </ListItem>

                  <ListItem
                    href="/exam-registration/jeongcheogi-application"
                    title="정처기 원서 접수 방법"
                  >
                    원서 접수 체크리스트를 확인해보세요
                  </ListItem>
                  <ListItem
                    href="/exam-registration/jeongcheogi-fee-discount"
                    title="정처기 응시료 50% 할인"
                  >
                    청년 50% 할인 정보
                  </ListItem>
                  <ListItem
                    href="/exam-registration/jeongcheogi-eligibility"
                    title="응시자격 서류 제출 확인 방법"
                  >
                    혹시 모르니 한 번 더 확인해봐요
                  </ListItem>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <NavigationMenuTrigger>
                <span className="inline-flex items-center gap-1">
                  <Sparkles className="h-4 w-4 shrink-0 text-amber-500" />
                  커뮤니티
                  {NAV_UPDATE_SIGNAL_VISIBILITY.community && (
                    <NavPingDot className="relative ml-0.5 h-1.5 w-1.5" />
                  )}
                </span>
              </NavigationMenuTrigger>
              <NavigationMenuContent className="right-0 left-auto">
                {/* 순서는 NAV_SECTIONS 커뮤니티 항목과 동일(2026-09-18): 감자단 잡담 → 질문·버그 게시판 →
                    시험 후기 담벼락 → 문제 포럼 → 두근두근 문제 복원. 전환기 인라인 surface 라 데이터는 복제. */}
                <ul className="grid gap-4 w-[620px] grid-cols-3 p-2">
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="relative flex h-full w-full flex-col justify-end rounded-md bg-amber-100 p-6 no-underline outline-hidden select-none ring-2 ring-amber-400 dark:bg-amber-900 dark:ring-amber-500 focus:shadow-md"
                        href="/community/talk"
                      >
                        <Image
                          src="/main/study_gamza.webp"
                          alt="감자단 잡담"
                          width={64}
                          height={64}
                          className="h-16 w-16"
                        />
                        <div className="mt-4 mb-2 flex items-center gap-2 text-base font-medium text-amber-950 dark:text-amber-50">
                          감자단 잡담
                          <NavBadgePill badge="NEW" />
                        </div>
                        <p className="text-sm leading-tight text-amber-900/80 dark:text-amber-100/80">
                          정처기 준비하며 나누고 싶은<br />이야기를 자유롭게
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/community/issues"
                      >
                        <Image
                          src="/main/shocking_gamza.webp"
                          alt="질문·버그 게시판"
                          width={100}
                          height={100}
                        />
                        <div className="mt-4 mb-2 flex items-center gap-2 text-base font-medium">
                          질문·버그 게시판
                          <NavBadgePill badge="NEW" />
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          버그 제보·요약본 오류·질문을<br />남기고 답변을 받아요
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/community"
                      >
                        <Image
                          src="/main/happy_gamza.webp"
                          alt="시험 후기 담벼락"
                          width={100}
                          height={100}
                        />
                        <div className="mt-4 mb-2 text-base font-medium">
                          시험 후기 담벼락
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          짧은 후기가 모여<br />큰 글자가 됩니다
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/forum/jcg"
                      >
                        <Image
                          src="/main/eureka_gamza.webp"
                          alt="문제 포럼"
                          width={100}
                          height={100}
                        />
                        <div className="mt-4 mb-2 text-base font-medium">
                          문제 포럼
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          정처기 문제별<br />질문과 토론
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/community/restoration"
                      >
                        <Image
                          src="/main/restoration_gamja.webp"
                          alt="두근두근 문제 복원"
                          width={64}
                          height={64}
                          className="h-16 w-16"
                        />
                        <div className="mt-4 mb-2 text-base font-medium">
                          두근두근 문제 복원
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          실시간으로 시험 문제 복원하고 다같이 가채점해봐요
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
            <NavigationMenuItem>
              {/* 요약 PDF UPDATE 캠페인 토글이 켜지면 halo 없는 '정적 도트'를 노출한다.
                  menu-grid.md §4-5 는 ping 도트를 "한 nav 바에 1개 트리거로 제한(포커스 분산 방지)"하는데
                  그 1개는 이미 모의고사(파이널 새 회차 오픈)가 쓰고 있다. 그래서 ping 은 양보하고 한 단계
                  낮은 정적 도트로 알린다 — reduced-motion 사용자가 보는 모습과 같은 상태라 새 시각 언어가 아니다.
                  amber 배경 강조는 붙이지 않는다(§4-3: 배경 클래스가 과도하면 강조 의미가 희석됨).
                  도트는 decorative(aria-hidden) 이고 의미는 드롭다운 안 UPDATE/곧 마감 뱃지가 전달한다. */}
              <NavigationMenuTrigger>
                <span className="inline-flex items-center gap-1">
                  감자 마켓
                  {NAV_UPDATE_SIGNAL_VISIBILITY.market && (
                    <NavStaticDot className="ml-0.5 h-1.5 w-1.5" />
                  )}
                </span>
              </NavigationMenuTrigger>
              <NavigationMenuContent className="right-0 left-auto">
                <ul className="grid gap-4 w-[420px] grid-cols-2 p-2">
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/membership"
                      >
                        <Image
                          src="/main/3d_no-bg_butler_half-clothes_gamja.webp"
                          alt="감자 이용권"
                          width={100}
                          height={100}
                        />
                        <div className="mt-4 mb-2 flex items-center gap-2 text-base font-medium">
                          감자 이용권
                          {/* 판매 종료 임박 구간에만 노출 — 판매 종료(pass-sales salesEndAtISO) 후 자동으로 사라진다 */}
                          {passSalesClosingSoon && <NavBadgePill badge="곧 마감" />}
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          광고 제거, 요약 PDF 등<br />프리미엄 기능을 이용하세요
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                  <li>
                    <NavigationMenuLink asChild>
                      <Link
                        className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                        href="/summary-pdf"
                      >
                        <Image
                          src="/main/3d_super_compressed_gamja.webp"
                          alt="요약 PDF"
                          width={100}
                          height={100}
                        />
                        <div className="mt-4 mb-2 flex items-center gap-2 text-base font-medium">
                          요약 PDF
                          {/* 2026년 3회 요약 PDF 출시. 데스크탑 hero 카드는 NAV_SECTIONS 를 소비하지 않는
                              전환기 surface 라 파이널 모의고사 카드와 동일하게 인라인으로 둔다 */}
                          {NAV_UPDATE_SIGNAL_VISIBILITY.market && (
                            <NavBadgePill badge="UPDATE" />
                          )}
                        </div>
                        <p className="text-muted-foreground text-sm leading-tight">
                          2026년 3회 실기 대비<br />이론·코딩 요약 PDF
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </li>
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
            <NavigationMenuItem className="empty:hidden">
              {/* 답글 알림 벨 (JCG-3601) — 프로필 왼쪽. 비로그인이면 컴포넌트가 null 을 반환하므로 빈 li 는 숨겨 gap 을 없앤다 */}
              <NotificationBell variant="header" />
            </NavigationMenuItem>
            <NavigationMenuItem>
              {/* 클래식 레이아웃(셸 밖) 전용 NAV — 태블릿(640~1023px)엔 IconRail 내 메뉴가 없으므로
                  사용자 버튼에 /my 커스텀 항목을 노출한다(JCG-2160 의 '데스크탑은 Clerk 기본만' 정책은
                  셸 IconRail 전용으로 유지). */}
              <AuthSection iconClassName="h-[1.2rem] w-[1.2rem]" />
            </NavigationMenuItem>
            <NavigationMenuItem>
              <SettingsButton />
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {/* Mobile Menu */}
      {/* 데스크탑 NavigationMenu 는 폭 ~825px 로 셸 게이트(1024px) 미만에선 수평 overflow 를 일으키므로 lg 부터만 노출 — 640~1023px 태블릿은 모바일 드롭다운 사용(JCG-2224). */}
      <div className="lg:hidden flex justify-end items-center gap-2">
        {/* 답글 알림 벨 (JCG-3601) — 모바일은 프로필 좌측 */}
        <NotificationBell variant="header" />
        <AuthSection iconClassName="h-8 w-8" />
        <SettingsButton />
        <DropdownMenu
          modal={false}
          open={mobileMenuOpen}
          onOpenChange={setMobileMenuOpen}
        >
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="iconLg">
              <MenuIcon className="h-10 w-10" />
              <span className="sr-only">메뉴 열기</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* SSOT(NAV_SECTIONS) 소비 — 데스크탑 NavigationMenu/hero 카드는 추후 제거 예정이라 미연동 */}
            {NAV_SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <DropdownMenuSub key={section.key}>
                  <DropdownMenuSubTrigger
                    className={cn(
                      "gap-1.5",
                      section.accent && "bg-amber-50 dark:bg-amber-950",
                    )}
                  >
                    {section.accent ? (
                      <span className="flex min-w-0 items-center gap-1">
                        <Icon className="h-4 w-4 text-amber-500" />
                        {section.label}
                      </span>
                    ) : (
                      <span>{section.label}</span>
                    )}
                    {/* 섹션 트리거 자체의 강조 뱃지(파이널 새 회차 UPDATE 등) — 데스크톱 도트와 톤 통일 */}
                    {section.badge && <NavBadgePill badge={section.badge} tone={section.badgeTone} />}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {section.items.map((item) => {
                      // 데스크탑 hero 카드와 같은 신호를 모바일 행에도 노출(surface 간 정보 불일치 방지)
                      const resolved = withTimedNavBadge(item, passSalesClosingSoon);
                      return (
                        <MobileNavItem
                          key={getNavSubLinkKey(resolved)}
                          item={resolved}
                        />
                      );
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
